import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Select, DatePicker, TimePicker, Tag, Button, Carousel, Row, Col, Card, Typography, Skeleton, message } from 'antd';
import dayjs from 'dayjs';
// Using plain elements with Tailwind classes
import { StarIcon, MapPinIcon, ClockIcon, UsersIcon } from '@heroicons/react/24/solid';
import GetStarted from './GetStarted';
import { resolveImageSrc, handleImageError, getImageForCard } from '../../utils/images';
import api from '../../services/api';
import { useDynamicContent, getContentByKey } from '../../hooks/useDynamicContent';
import { useHomepageContent } from '../../hooks/useDynamicContent';
import RichTextRenderer from '../UI/RichTextRenderer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

// Custom styles for the hero carousel and food decorations
const carouselStyles = `
    .hero-carousel .ant-carousel {
        border-radius: 16px !important;
        overflow: hidden !important;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        width: 100% !important;
        max-width: 100% !important;
    }
    
    .hero-carousel .ant-carousel-content {
        border-radius: 16px !important;
        overflow: hidden !important;
        width: 100% !important;
        max-width: 100% !important;
    }
    
    .hero-carousel .slick-slider {
        width: 100% !important;
        max-width: 100% !important;
    }
    
    .hero-carousel .slick-list {
        width: 100% !important;
        max-width: 100% !important;
    }
    
    .hero-carousel .slick-track {
        width: 100% !important;
        max-width: 100% !important;
    }
    
    .custom-dots {
        bottom: 20px !important;
        z-index: 10 !important;
    }
    
    .custom-dots .ant-carousel-dot {
        width: 14px !important;
        height: 14px !important;
        border-radius: 50% !important;
        background: rgba(255, 255, 255, 0.3) !important;
        border: 2px solid rgba(255, 255, 255, 0.5) !important;
        margin: 0 8px !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        backdrop-filter: blur(8px) !important;
    }
    
    .custom-dots .ant-carousel-dot-active {
        background: rgba(255, 255, 255, 0.9) !important;
        border-color: white !important;
        transform: scale(1.3) !important;
        box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3) !important;
    }
    
    .custom-dots .ant-carousel-dot:hover {
        background: rgba(255, 255, 255, 0.6) !important;
        transform: scale(1.15) !important;
        box-shadow: 0 2px 8px rgba(255, 255, 255, 0.2) !important;
    }
    
    /* Smooth transitions for carousel slides */
    .hero-carousel .ant-carousel-content .slick-slide {
        transition: all 0.5s ease-in-out !important;
    }
    
    /* Enhanced image loading animation */
    .hero-carousel img {
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out !important;
    }
    
    .hero-carousel img:hover {
        transform: scale(1.02) !important;
    }
    
    /* Responsive adjustments */
    @media (max-width: 1024px) {
        .hero-carousel .ant-carousel {
            border-radius: 14px !important;
        }
        
        .custom-dots {
            bottom: 18px !important;
        }
        
        .custom-dots .ant-carousel-dot {
            width: 13px !important;
            height: 13px !important;
            margin: 0 7px !important;
        }
    }
    
    @media (max-width: 768px) {
        .hero-carousel .ant-carousel {
            border-radius: 12px !important;
        }
        
        .custom-dots {
            bottom: 15px !important;
        }
        
        .custom-dots .ant-carousel-dot {
            width: 12px !important;
            height: 12px !important;
            margin: 0 6px !important;
        }
    }
    
    @media (max-width: 640px) {
        .hero-carousel .ant-carousel {
            border-radius: 10px !important;
        }
        
        .custom-dots {
            bottom: 12px !important;
        }
        
        .custom-dots .ant-carousel-dot {
            width: 11px !important;
            height: 11px !important;
            margin: 0 5px !important;
        }
    }
    
    @media (max-width: 480px) {
        .hero-carousel .ant-carousel {
            border-radius: 8px !important;
        }
        
        .custom-dots {
            bottom: 10px !important;
        }
        
        .custom-dots .ant-carousel-dot {
            width: 10px !important;
            height: 10px !important;
            margin: 0 4px !important;
        }
    }
    
    @media (max-width: 360px) {
        .hero-carousel .ant-carousel {
            border-radius: 6px !important;
        }
        
        .custom-dots {
            bottom: 8px !important;
        }
        
        .custom-dots .ant-carousel-dot {
            width: 9px !important;
            height: 9px !important;
            margin: 0 3px !important;
        }
    }
    
    /* Food decoration animations */
    .floating-food {
        position: absolute;
        opacity: 0.1;
        animation: float 6s ease-in-out infinite;
        pointer-events: none;
        z-index: 1;
    }
    
    .floating-food:nth-child(odd) {
        animation-delay: -2s;
        animation-duration: 8s;
    }
    
    .floating-food:nth-child(even) {
        animation-delay: -4s;
        animation-duration: 10s;
    }
    
    @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        25% { transform: translateY(-20px) rotate(5deg); }
        50% { transform: translateY(-10px) rotate(-3deg); }
        75% { transform: translateY(-15px) rotate(2deg); }
    }
    
    .food-bounce {
        animation: bounce 3s ease-in-out infinite;
    }
    
    @keyframes bounce {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
    }
    
    .food-spin {
        animation: spin 20s linear infinite;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    
    .food-pulse {
        animation: pulse 2s ease-in-out infinite;
    }
    
    @keyframes pulse {
        0%, 100% { opacity: 0.1; transform: scale(1); }
        50% { opacity: 0.3; transform: scale(1.1); }
    }
`;

// DecorSection wrapper (no emojis)
const DecorSection = ({ children, context }) => (
    <div data-emoji-context={context}>
        {children}
    </div>
);

function Home() {
    const [heroImg, setHeroImg] = useState('');
    useEffect(() => { try { setHeroImg(localStorage.getItem('hero_img_home') || ''); } catch (_) { } }, []);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cities, setCities] = useState([]);
    const [hideCitySelect, setHideCitySelect] = useState(false);
    const [form, setForm] = useState({
        city_id: '',
        occasion: '',
        services: [],
        date: '',
        time: '',
        guestsVeg: 0,
        guestsNonVeg: 0,
    });

    // Dynamic content
    const { content: homepageContent, loading: contentLoading } = useHomepageContent();

    useEffect(() => {
        fetchVendors();
        fetchCities();
        fetchHeroSlides();
        fetchBanners();
        fetchFeatures();
        fetchTestimonials();
        fetchArticles();
        fetchFeaturedMenus();
        fetchStats();
    }, []);

    const [heroSlides, setHeroSlides] = useState([]);
    const [banners, setBanners] = useState([]);
    const [bannersInitialized, setBannersInitialized] = useState(false);
    // Dynamic content fallbacks (admin-configurable)
    const { content: heroDC } = useDynamicContent('hero');
    const { content: bannersDC } = useDynamicContent('banners');
    const [features, setFeatures] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [articles, setArticles] = useState([]);
    const [featuredMenus, setFeaturedMenus] = useState([]);
    const [stats, setStats] = useState({
        total_bookings: 0,
        total_vendors: 0,
        total_reviews: 0,
        average_rating: 0,
        years_experience: 15,
        expert_chefs: 50,
    });

    const parseJsonArray = (raw) => {
        try {
            if (!raw) return [];
            const val = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return Array.isArray(val) ? val : [];
        } catch {
            return [];
        }
    };

    const fetchHeroSlides = async () => {
        try {
            // First try to load from content management system
            try {
                const contentRes = await api.getContentByKey('hero_slides').catch(() => ({ success: false }));
                console.log('Homepage hero content response:', contentRes);
                
                if (contentRes.success && contentRes.data) {
                    // Handle both direct content and nested structure
                    let contentData = contentRes.data.content || contentRes.data.hero_slides?.content;
                    if (contentData) {
                        const parsed = parseJsonArray(contentData);
                        if (parsed.length) {
                            console.log('Loaded hero slides from content management:', parsed);
                            setHeroSlides(parsed);
                            return;
                        }
                    }
                }
            } catch (err) {
                console.log('Failed to load from content management:', err);
            }

            // Fallback to API endpoint
            const res = await api.getHeroSlides().catch(() => ({ data: [] }));
            const arr = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            if (arr && arr.length) {
                console.log('Loaded hero slides from API:', arr);
                setHeroSlides(arr);
                return;
            }
            
            // Final fallback to dynamic content key (JSON string or array)
            const dc = getContentByKey(heroDC, 'hero_slides') || getContentByKey(heroDC, 'hero_slides_json');
            const parsed = parseJsonArray(dc);
            if (parsed.length) {
                console.log('Loaded hero slides from dynamic content:', parsed);
                setHeroSlides(parsed);
            }
        } catch (err) {
            console.error('Error loading hero slides:', err);
        }
    };

    // Fallback when dynamic content loads later
    useEffect(() => {
        try {
            if (!heroSlides || heroSlides.length === 0) {
                const dc = getContentByKey(heroDC, 'hero_slides') || getContentByKey(heroDC, 'hero_slides_json');
                const parsed = parseJsonArray(dc);
                if (parsed.length) setHeroSlides(parsed);
            }
        } catch (_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [heroDC]);

    const fetchBanners = async () => {
        try {
            // First try to load from content management system
            try {
                const contentRes = await api.getContentByKey('banners_media').catch(() => ({ success: false }));
                console.log('Homepage banner content response:', contentRes);
                
                if (contentRes.success && contentRes.data) {
                    // Handle both direct content and nested structure
                    let contentData = contentRes.data.content || contentRes.data.banners_media?.content;
                    if (contentData) {
                        const parsed = parseJsonArray(contentData);
                        // Filter out invalid banners
                        const validBanners = parsed.filter(bn => bn && (bn.variants?.large || bn.url || bn.file_path));
                        if (validBanners.length) {
                            console.log('Loaded banners from content management:', validBanners);
                            setBanners(validBanners);
                            return;
                        }
                    }
                }
            } catch (err) {
                console.log('Failed to load banners from content management:', err);
            }

            // Fallback to API endpoint
            const res = await api.getPublicMediaByCategory('banner').catch(() => ({ data: [] }));
            const list = res.data?.data || res.data || [];
            if (Array.isArray(list) && list.length) {
                // Filter out invalid banners
                const validBanners = list.filter(bn => bn && (bn.variants?.large || bn.url || bn.file_path));
                if (validBanners.length) {
                    console.log('Loaded banners from API:', validBanners);
                    setBanners(validBanners);
                    return;
                }
            }
            
            // Final fallback to dynamic content key (JSON string or array)
            const dc = getContentByKey(bannersDC, 'banners_media') || getContentByKey(bannersDC, 'banners');
            const parsed = parseJsonArray(dc);
            // Filter out invalid banners
            const validBanners = parsed.filter(bn => bn && (bn.variants?.large || bn.url || bn.file_path));
            if (validBanners.length) {
                console.log('Loaded banners from dynamic content:', validBanners);
                setBanners(validBanners);
            } else {
                // Explicitly set to empty array if no valid banners
                setBanners([]);
            }
        } catch (err) {
            console.error('Error loading banners:', err);
            // Explicitly set to empty array on error
            setBanners([]);
        } finally {
            setBannersInitialized(true);
        }
    };

    // Fallback when dynamic content loads later
    useEffect(() => {
        try {
            if (!banners || banners.length === 0) {
                const dc = getContentByKey(bannersDC, 'banners_media') || getContentByKey(bannersDC, 'banners');
                const parsed = parseJsonArray(dc);
                // Filter out invalid banners
                const validBanners = parsed.filter(bn => bn && (bn.variants?.large || bn.url || bn.file_path));
                if (validBanners.length) {
                    setBanners(validBanners);
                } else {
                    setBanners([]);
                }
            }
        } catch (_) {
            setBanners([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bannersDC]);

    const fetchFeatures = async () => {
        try {
            console.log('Fetching homepage features...');
            const res = await api.getHomepageFeatures(4);
            console.log('Features response:', res);
            setFeatures(res.data || []);
        } catch (err) {
            console.error('Failed to fetch features:', err);
            console.error('Error details:', err.message, err.stack);
            // Fallback to hardcoded features
            setFeatures([
                {
                    id: 1,
                    name: 'Premium Catering',
                    description: 'Professional catering services for all occasions',
                    icon: null,
                },
                {
                    id: 2,
                    name: 'Best Catering Experience',
                    description: 'Seamless booking process and delivery',
                    icon: '🏙️',
                },
                {
                    id: 3,
                    name: 'Quality Assurance',
                    description: 'Verified vendors with quality guarantees',
                    icon: '⭐',
                },
                {
                    id: 4,
                    name: 'Easy Booking',
                    description: 'Simple and secure online booking process',
                    icon: '📱',
                },
            ]);
        }
    };

    const fetchTestimonials = async () => {
        try {
            const res = await api.getFeaturedTestimonials(3);
            setTestimonials(res.data || []);
        } catch (err) {
            console.error('Failed to fetch testimonials:', err);
            // Fallback to hardcoded testimonials
            setTestimonials([
                {
                    id: 1,
                    name: 'Priya Sharma',
                    location: 'Mumbai',
                    content: 'Excellent service! The food was delicious and the delivery was on time. Highly recommended!',
                    rating: 5,
                },
                {
                    id: 2,
                    name: 'Rajesh Kumar',
                    location: 'Delhi',
                    content: 'Great experience with Spoon & Spice. Professional team and amazing food quality.',
                    rating: 5,
                },
                {
                    id: 3,
                    name: 'Anita Patel',
                    location: 'Bangalore',
                    content: 'Perfect for our corporate event. Everything was handled professionally.',
                    rating: 5,
                },
            ]);
        }
    };

    const fetchArticles = async () => {
        try {
            const res = await api.getLatestArticles('news', 3);
            setArticles(res.data || []);
        } catch (err) {
            console.error('Failed to fetch articles:', err);
            // Fallback to hardcoded articles
            setArticles([
                {
                    id: 1,
                    title: 'Feature Story 1',
                    excerpt: 'Discover our latest menu additions, city expansions, and seasonal offers curated for your events.',
                    featured_image: null,
                    slug: 'feature-story-1',
                },
                {
                    id: 2,
                    title: 'Feature Story 2',
                    excerpt: 'Discover our latest menu additions, city expansions, and seasonal offers curated for your events.',
                    featured_image: null,
                    slug: 'feature-story-2',
                },
                {
                    id: 3,
                    title: 'Feature Story 3',
                    excerpt: 'Discover our latest menu additions, city expansions, and seasonal offers curated for your events.',
                    featured_image: null,
                    slug: 'feature-story-3',
                },
            ]);
        }
    };

    const fetchFeaturedMenus = async () => {
        try {
            const res = await api.getMenus({ per_page: 6, featured: true });
            setFeaturedMenus(res.data?.data || res.data || []);
        } catch (err) {
            console.error('Failed to fetch featured menus:', err);
            // Fallback to empty array
            setFeaturedMenus([]);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await api.getPublicStats();
            if (res.success && res.data) {
                setStats({
                    total_bookings: res.data.total_bookings || 0,
                    total_vendors: res.data.total_vendors || 0,
                    total_reviews: res.data.total_reviews || 0,
                    average_rating: res.data.average_rating || 0,
                    years_experience: res.data.years_experience || 15,
                    expert_chefs: res.data.expert_chefs || 50,
                });
            }
        } catch (err) {
            console.error('Failed to fetch statistics:', err);
            // Keep default values
        }
    };

    const fetchVendors = async () => {
        try {
            const response = await api.getVendors({ per_page: 6 });
            const list = response.data.data || [];
            setVendors(list);
        } catch (err) {
            setError('Failed to load vendors');
            console.error('Error fetching vendors:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCities = async () => {
        try {
            const response = await api.getCities();
            const list = response.data || [];
            setCities(list);
            if (list.length === 1) {
                setHideCitySelect(true);
                setForm((prev) => ({ ...prev, city_id: String(list[0].id) }));
                localStorage.setItem('selected_city_id', String(list[0].id));
            }
        } catch (err) {
            console.error('Error fetching cities:', err);
        }
    };



    const occasions = [
        'House Party',
        'Birthday',
        'Premium',
        'Office',
        'Anniversary',
        'Family Gathering',
        'Wedding',
        'Festival',
    ];

    const servicesList = [
        '8 Hr Delivery',
        'Bulk Food',
        'Buffet Services',
        'Live Experiences',
        'Customize',
        'Live Bread',
        'Snack Box',
        'MealBox',
    ];

    const toggleService = (label) => {
        setForm((prev) => {
            const next = new Set(prev.services);
            if (next.has(label)) next.delete(label); else next.add(label);
            return { ...prev, services: Array.from(next) };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Navigate to menu with filters encoded in query
        const params = new URLSearchParams({
            city_id: form.city_id || '',
            occasion: form.occasion || '',
            date: form.date || '',
            time: form.time || '',
            guestsVeg: String(form.guestsVeg || 0),
            guestsNonVeg: String(form.guestsNonVeg || 0),
            services: form.services.join(','),
        }).toString();
        window.location.href = `/menu?${params}`;
    };

    return (
        <div className="min-h-screen">
            <style>{carouselStyles}</style>
            {/* Hero Section - Split Layout Inspired by Savoria */}
            <div className="relative overflow-hidden hero-modern py-12 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        {/* Left Side - Text Content */}
                        <div className="space-y-6">
                            {/* Premium Tag */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5F3F0] rounded-full">
                                <span className="text-[#C97D60] text-lg">★</span>
                                <span className="text-sm font-medium text-[#4A4A4A]">
                                    {contentLoading ? (
                                        <Skeleton.Input active size="small" style={{ width: 150 }} />
                                    ) : (
                                        getContentByKey(homepageContent, 'homepage_hero_tag', 'Premium Catering Services')
                                    )}
                                </span>
                            </div>

                            {/* Main Headline */}
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight" style={{ fontFamily: 'var(--font-serif)' }}>
                                {contentLoading ? (
                                    <Skeleton.Input active size="large" style={{ width: '90%' }} />
                                ) : (
                                    <>
                                        <span className="text-[#2C2C2C]">
                                            {getContentByKey(homepageContent, 'homepage_hero_title_part1', 'Crafting')}
                                        </span>{' '}
                                        <span className="text-[#C97D60]">
                                            {getContentByKey(homepageContent, 'homepage_hero_title_part2', 'Unforgettable Culinary')}
                                        </span>{' '}
                                        <span className="text-[#2C2C2C]">
                                            {getContentByKey(homepageContent, 'homepage_hero_title_part3', 'Moments')}
                                        </span>
                                    </>
                                )}
                            </h1>

                            {/* Description */}
                            <p className="text-lg md:text-xl text-[#4A4A4A] leading-relaxed max-w-xl">
                                {contentLoading ? (
                                    <Skeleton.Input active size="default" style={{ width: '100%' }} />
                                ) : (
                                    getContentByKey(homepageContent, 'homepage_hero_subtitle', 'From intimate gatherings to grand celebrations, we bring exquisite flavors and impeccable service to every event we cater.')
                                )}
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 pt-2">
                                <Link
                                    to="/get-started"
                                    className="inline-flex items-center justify-center px-8 py-4 bg-[#C97D60] text-white rounded-lg font-semibold hover:bg-[#B8735A] transition-all duration-300 shadow-md hover:shadow-lg"
                                >
                                    Plan Your Event
                                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                                <Link
                                    to="/menu"
                                    className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-[#C97D60] border-2 border-[#C97D60] rounded-lg font-semibold hover:bg-[#C97D60]/5 transition-all duration-300"
                                >
                                    Explore Menu
                                </Link>
                            </div>

                            {/* Statistics */}
                            <div className="flex flex-wrap gap-6 pt-4">
                                <div>
                                    <div className="text-2xl font-bold text-[#2C2C2C]">
                                        {stats.total_bookings > 0 ? `${stats.total_bookings}+` : '500+'}
                                    </div>
                                    <div className="text-sm text-[#4A4A4A]">Events Catered</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-[#2C2C2C]">
                                        {stats.years_experience > 0 ? `${stats.years_experience}+` : '15+'}
                                    </div>
                                    <div className="text-sm text-[#4A4A4A]">Years Experience</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-[#2C2C2C]">
                                        {stats.expert_chefs > 0 ? `${stats.expert_chefs}+` : '50+'}
                                    </div>
                                    <div className="text-sm text-[#4A4A4A]">Expert Chefs</div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Food Collage with Rating Card */}
                        <div className="relative">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-[#F5F3F0]">
                                {(heroSlides && heroSlides.length > 0 && (heroSlides[0]?.image || heroSlides[0]?.url)) ? (
                                    <img
                                        src={resolveImageSrc(heroSlides[0].image || heroSlides[0].url)}
                                        alt="Gourmet dishes"
                                        className="w-full h-[400px] md:h-[500px] object-cover"
                                        onError={(e) => {
                                            handleImageError(e);
                                            e.target.style.display = 'none';
                                            e.target.parentElement.classList.add('bg-gradient-to-br', 'from-[#C97D60]/10', 'to-[#B8735A]/5');
                                        }}
                                    />
                                ) : heroImg ? (
                                    <img
                                        src={resolveImageSrc(heroImg)}
                                        alt="Gourmet dishes"
                                        className="w-full h-[400px] md:h-[500px] object-cover"
                                        onError={(e) => {
                                            handleImageError(e);
                                            e.target.style.display = 'none';
                                            e.target.parentElement.classList.add('bg-gradient-to-br', 'from-[#C97D60]/10', 'to-[#B8735A]/5');
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-[400px] md:h-[500px] bg-gradient-to-br from-[#C97D60]/10 to-[#B8735A]/5 flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-[#C97D60]/20 flex items-center justify-center">
                                                <svg className="w-12 h-12 text-[#C97D60]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                </svg>
                                            </div>
                                            <p className="text-sm text-[#4A4A4A] font-medium">Add hero image via Admin</p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Rating Card Overlay */}
                                <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-white/50">
                                    <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-5 h-5 text-[#C97D60]" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                        <span className="text-lg font-bold text-[#2C2C2C]">
                                            {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '4.9'}
                                        </span>
                                    </div>
                                    <div className="text-sm text-[#4A4A4A]">
                                        {stats.total_reviews > 0 ? `${stats.total_reviews}+ Reviews` : '500+ Reviews'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Get Started Section - Below Hero */}
            <div className="bg-white py-8 border-t border-[#EAE7E3]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <GetStarted
                        initialCityOptions={!hideCitySelect ? cities : []}
                        onComplete={(payload) => {
                            const params = new URLSearchParams(payload);
                            message.success('Details captured. Redirecting...');
                            window.location.href = `/get-started?${params.toString()}`;
                        }}
                    />
                </div>
            </div>

            {/* Our Services Section */}
            <div className="py-16 md:py-24 bg-[#FAF8F5] relative overflow-hidden section-spacing">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 fade-in-up">
                        <p className="text-sm md:text-base font-semibold uppercase tracking-wider text-[#C97D60] mb-3">
                            {contentLoading ? (
                                <Skeleton.Input active size="small" style={{ width: '150px', margin: '0 auto' }} />
                            ) : (
                                'WHAT WE OFFER'
                            )}
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-[#2C2C2C]" style={{ fontFamily: 'var(--font-serif)' }}>
                            {contentLoading ? (
                                <Skeleton.Input active size="default" style={{ width: '60%', margin: '0 auto' }} />
                            ) : (
                                getContentByKey(homepageContent, 'homepage_features_title', 'Our Services')
                            )}
                        </h2>
                        <p className="text-base md:text-lg text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
                            {contentLoading ? (
                                <Skeleton.Input active size="default" style={{ width: '80%', margin: '0 auto' }} />
                            ) : (
                                getContentByKey(homepageContent, 'homepage_features_subtitle', 'We provide comprehensive catering solutions designed to make your special moments truly memorable.')
                            )}
                        </p>
                    </div>
                    <Row gutter={[24, 24]}>
                        {features.length > 0 ? features.map((feature, index) => {
                            // Service icons mapping
                            const serviceIcons = {
                                0: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 2V8C3 10.21 4.79 12 7 12H9V14H7C4.79 14 3 15.79 3 18V22H5V18C5 16.9 5.9 16 7 16H9V18C9 19.1 9.9 20 11 20H13C14.1 20 15 19.1 15 18V16H17C18.1 16 19 16.9 19 18V22H21V18C21 15.79 19.21 14 17 14H15V12H17C19.21 12 21 10.21 21 8V2H19V8C19 9.1 18.1 10 17 10H7C5.9 10 5 9.1 5 8V2H3Z" stroke="#B87D52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ),
                                1: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#B87D52" strokeWidth="2"/>
                                        <path d="M16 2V6M8 2V6M3 10H21" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                ),
                                2: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="9" cy="7" r="4" stroke="#B87D52" strokeWidth="2"/>
                                        <path d="M3 21C3 17 6.58 14 11 14C15.42 14 19 17 19 21" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                        <circle cx="17" cy="11" r="2" stroke="#B87D52" strokeWidth="2"/>
                                        <path d="M19 13C19 12.45 18.55 12 18 12C17.45 12 17 12.45 17 13" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                ),
                                3: (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 3H23L21 12H3L1 3Z" stroke="#B87D52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M3 12L5 21H19L21 12" stroke="#B87D52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M8 12V17M16 12V17" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                )
                            };
                            
                            return (
                                <Col xs={24} sm={12} lg={6} key={feature.id || index}>
                                    <Card 
                                        className="h-full border border-[#EAE7E3] hover:border-[#C97D60]/30 transition-all duration-300 bg-white"
                                        style={{ borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                                    >
                                        <div className="flex flex-col items-start">
                                            <div className="w-14 h-14 rounded-lg bg-[#E0A77D] flex items-center justify-center mb-4">
                                                {serviceIcons[index] || (
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="12" cy="12" r="10" stroke="#B87D52" strokeWidth="2"/>
                                                    </svg>
                                                )}
                                            </div>
                                            <Typography.Title level={4} className="!mb-3 !text-[#2C2C2C] !font-bold !text-lg">
                                                {feature.name}
                                            </Typography.Title>
                                            <Typography.Paragraph className="!mb-0 !text-[#4A4A4A] !leading-relaxed !text-sm">
                                                {feature.description}
                                            </Typography.Paragraph>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        }) : (
                            // Fallback services if no features from API
                            [
                                { id: 1, name: 'Custom Menus', description: 'Tailored menus crafted to match your event theme and dietary preferences perfectly.' },
                                { id: 2, name: 'Event Planning', description: 'Full-service coordination from concept to execution, ensuring a seamless experience.' },
                                { id: 3, name: 'Professional Staff', description: 'Trained service teams that deliver hospitality with warmth and precision.' },
                                { id: 4, name: 'On-Site Setup', description: 'Complete setup and breakdown services so you can focus on enjoying your event.' }
                            ].map((service, index) => {
                                const serviceIcons = {
                                    0: (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 2V8C3 10.21 4.79 12 7 12H9V14H7C4.79 14 3 15.79 3 18V22H5V18C5 16.9 5.9 16 7 16H9V18C9 19.1 9.9 20 11 20H13C14.1 20 15 19.1 15 18V16H17C18.1 16 19 16.9 19 18V22H21V18C21 15.79 19.21 14 17 14H15V12H17C19.21 12 21 10.21 21 8V2H19V8C19 9.1 18.1 10 17 10H7C5.9 10 5 9.1 5 8V2H3Z" stroke="#B87D52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ),
                                    1: (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="3" y="4" width="18" height="18" rx="2" stroke="#B87D52" strokeWidth="2"/>
                                            <path d="M16 2V6M8 2V6M3 10H21" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    ),
                                    2: (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="9" cy="7" r="4" stroke="#B87D52" strokeWidth="2"/>
                                            <path d="M3 21C3 17 6.58 14 11 14C15.42 14 19 17 19 21" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                            <circle cx="17" cy="11" r="2" stroke="#B87D52" strokeWidth="2"/>
                                            <path d="M19 13C19 12.45 18.55 12 18 12C17.45 12 17 12.45 17 13" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    ),
                                    3: (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 3H23L21 12H3L1 3Z" stroke="#B87D52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M3 12L5 21H19L21 12" stroke="#B87D52" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M8 12V17M16 12V17" stroke="#B87D52" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    )
                                };
                                
                                return (
                                    <Col xs={24} sm={12} lg={6} key={service.id}>
                                        <Card 
                                            className="h-full border border-[#EAE7E3] hover:border-[#C97D60]/30 transition-all duration-300 bg-white"
                                            style={{ borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                                        >
                                            <div className="flex flex-col items-start">
                                                <div className="w-14 h-14 rounded-lg bg-[#E0A77D] flex items-center justify-center mb-4">
                                                    {serviceIcons[index]}
                                                </div>
                                                <Typography.Title level={4} className="!mb-3 !text-[#2C2C2C] !font-bold !text-lg">
                                                    {service.name}
                                                </Typography.Title>
                                                <Typography.Paragraph className="!mb-0 !text-[#4A4A4A] !leading-relaxed !text-sm">
                                                    {service.description}
                                                </Typography.Paragraph>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })
                        )}
                    </Row>
                </div>
            </div>

            {/* Banner Section - Only show if banners are initialized and valid */}
            {bannersInitialized && (() => {
                // Strict validation: filter banners with actual valid image URLs (not placeholders)
                const validBanners = Array.isArray(banners) 
                    ? banners.filter(bn => {
                        if (!bn || typeof bn !== 'object') return false;
                        const imageUrl = bn.variants?.large || bn.url || bn.file_path;
                        if (!imageUrl) return false;
                        if (typeof imageUrl !== 'string') return false;
                        const trimmed = imageUrl.trim();
                        // Check if source is valid (not empty, not null/undefined strings)
                        if (trimmed.length === 0 || trimmed === 'null' || trimmed === 'undefined' || trimmed.startsWith('data:null')) {
                            return false;
                        }
                        // Check if resolved URL would be a placeholder
                        const resolved = resolveImageSrc(trimmed);
                        if (!resolved || resolved === '/images/placeholder.svg' || resolved.includes('placeholder')) {
                            return false;
                        }
                        return true;
                    })
                    : [];
                
                // Only render if we have valid banners
                if (validBanners.length === 0) {
                    return null;
                }
                
                return (
                    <DecorSection context="pricing">
                        <div className="py-8">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                <Swiper
                                    modules={[Navigation, Pagination, Autoplay, A11y]}
                                    slidesPerView={1}
                                    spaceBetween={16}
                                    pagination={{ clickable: true }}
                                    autoplay={{ delay: 4500, disableOnInteraction: false }}
                                    loop={validBanners.length > 1}
                                    breakpoints={{
                                        640: { slidesPerView: 1 },
                                        768: { slidesPerView: 2 },
                                        1024: { slidesPerView: 3 }
                                    }}
                                    a11y={{ enabled: true }}
                                >
                                    {validBanners.map((bn, i) => {
                                        const imageUrl = resolveImageSrc(bn.variants?.large || bn.url || bn.file_path);
                                        // Double check - don't render if it's a placeholder
                                        if (!imageUrl || imageUrl === '/images/placeholder.svg' || imageUrl.includes('placeholder')) {
                                            return null;
                                        }
                                        
                                        return (
                                            <SwiperSlide key={bn.id || i}>
                                                <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200">
                                                    <img
                                                        src={imageUrl}
                                                        alt={bn.alt_text || 'Promotional banner'}
                                                        className="w-full h-[180px] sm:h-[200px] md:h-[240px] lg:h-[260px] object-contain object-center bg-white"
                                                        onError={(e) => {
                                                            // Hide the image and its container on error
                                                            e.target.style.display = 'none';
                                                            const slide = e.target.closest('.swiper-slide');
                                                            if (slide) {
                                                                slide.style.display = 'none';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </SwiperSlide>
                                        );
                                    })}
                                </Swiper>
                            </div>
                        </div>
                    </DecorSection>
                );
            })()}

            {/* Testimonials Section - Elegant Design */}
            <div className="py-16 md:py-24 bg-[#FAF8F5] relative overflow-hidden section-spacing">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="section-header-modern fade-in-up">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>What Our Customers Say</h2>
                        <p className="text-lg md:text-xl text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed">
                            Don't just take our word for it - hear from our satisfied customers
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {testimonials.length > 0 ? (
                            testimonials.map((testimonial, index) => (
                                <div 
                                    key={testimonial.id || index} 
                                    className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 card-hover-lift border border-[#EAE7E3] modern-food-card stagger-item"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                >
                                    <div className="flex items-center mb-4">
                                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                                            <StarIcon key={i} className="h-6 w-6 text-yellow-400" />
                                        ))}
                                    </div>
                                    <p className="text-[#4A4A4A] mb-6 leading-relaxed text-base">"{testimonial.content}"</p>
                                    <div className="text-sm border-t border-[#EAE7E3] pt-4">
                                        <p className="font-bold text-[#2C2C2C] text-base">{testimonial.name}</p>
                                        <p className="text-[#4A4A4A] mt-1">{testimonial.location}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full text-center text-[#4A4A4A]">
                                <p>No testimonials available at the moment.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Featured Menus Section */}
            {featuredMenus.length > 0 && (
                <div className="relative py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Section Header */}
                        <div className="text-center mb-14">
                            <h2 className="text-4xl font-extrabold text-[#2C2C2C] tracking-tight" style={{ fontFamily: 'var(--font-serif)' }}>Featured Menus</h2>
                            <p className="mt-4 text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                                Discover our most popular and delicious catering menus, carefully curated by our expert chefs.
                            </p>
                        </div>

                        {/* Menu Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {featuredMenus.map((menu) => (
                                <div
                                    key={menu.id}
                                    className="group relative bg-white rounded-xl shadow-lg border border-[#EAE7E3] overflow-hidden transition hover:shadow-xl hover:scale-[1.02]"
                                >
                                    {/* Menu Image */}
                                    <div className="relative h-48 overflow-hidden flex items-center justify-center bg-[#F5F3F0]">
                                        {console.log(resolveImageSrc(menu.image), 'resolveImageSrc(menu.image)')}
                                        {menu.image ? (
                                            <img
                                                src={getImageForCard(menu)}
                                                alt={menu.name}
                                                className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition duration-300"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-[#C97D60]/10 to-[#B8735A]/5 flex items-center justify-center">
                                                <svg className="w-16 h-16 text-[#C97D60]/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                </svg>
                                            </div>
                                        )}
                                        <div className="absolute top-4 right-4">
                                            <span className="bg-[#C97D60] text-white px-3 py-1 rounded-md text-sm font-semibold">
                                                {menu.category?.name || 'Menu'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Menu Content */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-semibold text-[#2C2C2C] group-hover:text-[#C97D60] transition mb-2">
                                            {menu.name}
                                        </h3>
                                        <p className="text-sm text-[#4A4A4A] line-clamp-2 mb-4">
                                            {menu.description || 'Delicious catering menu for your special occasion.'}
                                        </p>
                                        
                                        {/* Menu Details */}
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center space-x-4 text-sm text-[#4A4A4A]">
                                                <span className="flex items-center">
                                                    <UsersIcon className="h-4 w-4 mr-1" />
                                                    {menu.min_guests || 10}+ guests
                                                </span>
                                                <span className="flex items-center">
                                                    <ClockIcon className="h-4 w-4 mr-1" />
                                                    {menu.preparation_time || '2-3'} hours
                                                </span>
                                            </div>
                                        </div>

                                        {/* Price and Action */}
                                        <div className="flex items-center justify-end">
                                            <Link
                                                to={`/menu/${menu.id}`}
                                                className="px-4 py-2 bg-[#C97D60] text-white rounded-lg hover:bg-[#B8735A] transition font-medium"
                                            >
                                                View Menu
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* View All Button */}
                        <div className="text-center mt-12">
                            <Link
                                to="/menu"
                                className="inline-flex items-center px-6 py-3 bg-[#C97D60] text-white rounded-lg hover:bg-[#B8735A] transition font-semibold"
                            >
                                View All Menus
                                <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Latest News Section - Only show if there are articles */}
            {articles.length > 0 && (
                <div className="relative py-24 bg-[#FAF8F5]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Section Header */}
                        <div className="text-center mb-14">
                            <h2 className="text-4xl font-extrabold text-[#2C2C2C] tracking-tight" style={{ fontFamily: 'var(--font-serif)' }}>Latest News</h2>
                            <p className="mt-4 text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                                Stay updated with the latest from our catering partners, offers, and event highlights.
                            </p>
                        </div>

                        {/* Article Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                            {articles.map((article, index) => (
                                <div
                                    key={article.id || index}
                                    className="group relative bg-white rounded-xl shadow-md border border-[#EAE7E3] overflow-hidden transition hover:shadow-lg hover:scale-[1.01]"
                                >
                                    {/* Featured Image */}
                                    {article.featured_image ? (
                                        <img
                                            src={resolveImageSrc(article.featured_image)}
                                            alt={article.title}
                                            className="h-48 w-full object-cover"
                                        />
                                    ) : (
                                        <div className="h-48 bg-gradient-to-br from-[#C97D60]/20 to-[#B8735A]/10" />
                                    )}

                                    {/* Content */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-semibold text-[#2C2C2C] group-hover:text-[#C97D60] transition">
                                            {article.title}
                                        </h3>
                                        <p className="mt-3 text-sm text-[#4A4A4A] line-clamp-3">{article.excerpt}</p>
                                        <Link
                                            to={article.slug ? `/articles/${article.slug}` : '/blog'}
                                            className="inline-block mt-4 text-[#C97D60] hover:text-[#B8735A] font-medium transition"
                                        >
                                            Read more →
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <section className="relative isolate overflow-hidden bg-gradient-to-r from-[#2C2C2C] via-[#3A3A3A] to-[#2C2C2C] py-24 md:py-32 section-spacing">
                {/* Content */}
                <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 fade-in-up">
                    <div className="rounded-3xl p-8 md:p-12 bg-white/10 backdrop-blur-md border border-white/20 shadow-2xl text-center">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                            Ready to Plan Your Event?
                        </h2>
                        <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Let us help you craft a one-of-a-kind dining experience that your guests will never forget.
                        </p>

                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <Link
                                to="/get-started"
                                className="px-8 py-4 text-base font-semibold rounded-xl bg-white text-[#2C2C2C] shadow-lg hover:bg-[#FAF8F5] hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
                            >
                                Start Planning
                            </Link>
                            <Link
                                to="/contact"
                                className="px-8 py-4 text-base font-semibold rounded-xl border-2 border-white text-white hover:bg-white hover:text-[#2C2C2C] transition-all duration-300 hover:scale-105 inline-flex items-center justify-center"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Wave Transition */}
                <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none -z-10">
                    <svg viewBox="0 0 1440 80" className="w-full h-20" preserveAspectRatio="none">
                        <path
                            fill="#2C2C2C"
                            d="M0,0 C480,80 960,0 1440,80 L1440,160 L0,160 Z"
                        />
                    </svg>
                </div>
            </section>
        </div>
    );
}


export default Home;